import React from 'react';
import { Box, Typography } from '@mui/material';
import { ActionQueueItem, CampaignSummaryItem, areAllCampaignsHealthy } from '../../utils/operationsDashboardUtils';
import { OPS_COLORS } from '../../constants/operationsDashboardColors';

interface CompactOperationsFooterProps {
  campaignSummary: CampaignSummaryItem[];
  actionQueue: ActionQueueItem[];
  rows: any[];
}

const CompactOperationsFooter: React.FC<CompactOperationsFooterProps> = ({
  campaignSummary,
  actionQueue,
  rows,
}) => {
  const healthyCount = campaignSummary.filter((c) => c.scheduled > 0 && c.operationalScore >= 100).length;
  const totalScheduledCampaigns = campaignSummary.filter((c) => c.scheduled > 0).length;
  const coverageRequests = rows.filter((r) => r.coverageStatus === 'NOT_COVERED' && r.requiresCoverage).length;
  const coverageAssigned = rows.filter((r) => ['COVERED', 'PARTIALLY_COVERED'].includes(r.coverageStatus)).length;
  const openPositions = campaignSummary.reduce((sum, c) => sum + c.uncovered, 0);
  const pendingActions = actionQueue.length;
  const allHealthy = areAllCampaignsHealthy(campaignSummary);

  const items = [
    {
      label: 'Campaigns Healthy',
      value: allHealthy ? `${totalScheduledCampaigns}/${totalScheduledCampaigns}` : `${healthyCount}/${totalScheduledCampaigns}`,
      color: allHealthy ? OPS_COLORS.present : OPS_COLORS.warning,
    },
    { label: 'Coverage Requests', value: coverageRequests, color: coverageRequests > 0 ? OPS_COLORS.uncovered : OPS_COLORS.expected },
    { label: 'Coverage Assigned', value: coverageAssigned, color: OPS_COLORS.coverage },
    { label: 'Open Positions', value: openPositions, color: openPositions > 0 ? OPS_COLORS.critical : OPS_COLORS.present },
    { label: 'Actions Pending', value: pendingActions, color: pendingActions > 0 ? OPS_COLORS.risk : OPS_COLORS.present },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0,
        minHeight: 32,
        px: 1,
        py: 0.5,
        mt: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: '#fafbfc',
        overflowX: 'auto',
      }}
    >
      {items.map((item, idx) => (
        <Box
          key={item.label}
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.5,
            px: 1.25,
            borderRight: idx < items.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', fontWeight: 600 }}>
            {item.label}
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.72rem', color: item.color }}>
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default CompactOperationsFooter;
